import type { ChipProps } from '@locus-taxy/disco-ui';
import {
  resolveEnumLabel,
  type EnumColumnInfo,
  type RowExpressionContext,
} from '@locus-taxy/encore-ui-toolkit';

export type StatusChipColor = NonNullable<ChipProps['color']>;

/** A status value as the header chip draws it. */
export interface EnumPresentation {
  label: string;
  color?: StatusChipColor;
}

interface PresentationColumn {
  field: string;
  [key: string]: unknown;
}

const ENTITY_DATA_PREFIX = 'entityData.';

const CHIP_COLORS: readonly StatusChipColor[] = [
  'default',
  'primary',
  'muted',
  'info',
  'success',
  'caution',
  'warning',
  'destructive',
];

/** Column fields and status paths come with or without the `entityData.` prefix. */
export function stripEntityDataPrefix(path: string): string {
  return path.startsWith(ENTITY_DATA_PREFIX)
    ? path.slice(ENTITY_DATA_PREFIX.length)
    : path;
}

function isChipColor(value: unknown): value is StatusChipColor {
  return CHIP_COLORS.includes(value as StatusChipColor);
}

function findAdditionalInfo(
  columns: readonly PresentationColumn[] | undefined,
  fieldPath: string | undefined
): EnumColumnInfo | undefined {
  if (!fieldPath) {
    return undefined;
  }
  const field = stripEntityDataPrefix(fieldPath);
  const column = columns?.find(c => stripEntityDataPrefix(c.field) === field);
  return column?.additionalInfo as EnumColumnInfo | undefined;
}

/**
 * Resolves how an enum value is shown, from the metadata grid columns. The
 * backend has already merged the tenant's entityViewConfig
 * `fieldPresentationConfig` over the schema labels there, so this is the same
 * label and colour the grid draws for that value.
 *
 * The label goes through encore-ui-toolkit's `resolveEnumLabel` so a configured
 * `additionalInfo.labelExpression` (FEEL, `=` prefix) is evaluated against
 * `row` + `context` exactly as the grid cell does (DP-7885); without a rule it
 * falls back to the `enumProperties` label, then the raw value. The colour
 * always comes from the stored value's `enumProperties` entry.
 */
export function resolveEnumPresentation(
  columns: readonly PresentationColumn[] | undefined,
  fieldPath: string | undefined,
  value: string | undefined,
  row?: Record<string, unknown>,
  context?: RowExpressionContext
): EnumPresentation | undefined {
  if (!value) {
    return undefined;
  }
  const additionalInfo = findAdditionalInfo(columns, fieldPath);
  const property = additionalInfo?.enumProperties?.[value];
  return {
    label: resolveEnumLabel({ value, additionalInfo, row, context }),
    color: isChipColor(property?.color) ? property.color : undefined,
  };
}
